import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx } from "react/jsx-runtime";
import { useMemo, useRef } from 'react';
import { useOverlayContext } from 'seagull';
import { getTestIds } from 'seagull/utils/TestIds';
import { useStoreActions } from '~/contexts';
import { useLocaleKeys } from '~/i18n';
import { ConversationModel } from '~/models';
import { BulkPaneShell } from '../conversation/BulkPaneShell';
import { useBulkAgentPane } from '../conversation/useBulkAgentPane';
import { assistantEnvelopeUpdates } from './utils/assistantEnvelopeUpdates';
import { latestUserInstruction } from './utils/latestUserInstruction';
export const GetBulkAmendmentAgentPaneTID = (parentTestId)=>getTestIds(parentTestId);
export const BulkAmendmentAgentPane = (param)=>{
    let { scope, agreementNames, onFilterUpdate, onSelectionUpdate, onScopeRestore, onAgreementsChanged, isOpen, onOpenChange, testId = '' } = param;
    const { t } = useLocaleKeys();
    const { BulkAmendmentAgentPaneTestIds } = useMemo(()=>GetBulkAmendmentAgentPaneTID(testId), [
        testId
    ]);
    const { api: storeApi } = useStoreActions();
    const replayed = useRef({
        filter: false,
        selection: false
    });
    const { showErrorToast } = useOverlayContext();
    const { conversation, handlers, sendPrompt, lastInstruction } = useBulkAgentPane({
        api: {
            get: (conversationId)=>storeApi.agenticBulkAmendment.get(conversationId),
            sendMessage: (args)=>storeApi.agenticBulkAmendment.sendMessage(_object_spread_props(_object_spread({}, args), {
                    scope
                })),
            undo: (args)=>storeApi.agenticBulkAmendment.undo(args)
        },
        createConversation: ()=>storeApi.agenticBulkAmendment.create({
                matchedAgreementCount: scope.matchedAgreementCount,
                agreementNames,
                selectedAgreementIds: scope.selectedAgreementIds,
                currentFilter: scope.currentFilter
            }),
        restoreMostRecent: ()=>storeApi.agenticBulkAmendment.recent(),
        onScopeRestored: (restoredScope)=>onScopeRestore === null || onScopeRestore === void 0 ? void 0 : onScopeRestore({
                currentFilter: replayed.current.filter ? null : restoredScope.currentFilter,
                selectedAgreementIds: replayed.current.selection ? null : restoredScope.selectedAgreementIds
            }),
        sendFeedback: (args)=>storeApi.agenticBulkAmendment.feedback(args),
        onAssistantMessage: handleAssistantMessage,
        onHydrated: applyPersistedUpdates,
        onUndoUnavailable: ()=>showErrorToast(t.agenticDrafting.errors.undoUnavailable(), BulkAmendmentAgentPaneTestIds.UndoUnavailableToast()),
        isOpen,
        feedbackToastTestId: BulkAmendmentAgentPaneTestIds.FeedbackSuccessToast()
    });
    function applyPersistedUpdates(messages) {
        const restored = ConversationModel.ofMessages(messages);
        const filterUpdate = restored.latestPersistedAgreementFilterUpdate();
        if (filterUpdate != null) onFilterUpdate === null || onFilterUpdate === void 0 ? void 0 : onFilterUpdate(filterUpdate, latestUserInstruction(messages));
        const selectionUpdate = restored.latestPersistedSelectionUpdate();
        if (selectionUpdate != null && selectionUpdate.length > 0) onSelectionUpdate === null || onSelectionUpdate === void 0 ? void 0 : onSelectionUpdate(selectionUpdate);
        replayed.current = {
            filter: filterUpdate != null,
            selection: selectionUpdate != null && selectionUpdate.length > 0
        };
    }
    function handleAssistantMessage(message) {
        const updates = assistantEnvelopeUpdates(message);
        if (updates.filterUpdate != null) onFilterUpdate === null || onFilterUpdate === void 0 ? void 0 : onFilterUpdate(updates.filterUpdate, lastInstruction());
        if (updates.selectionUpdate != null) onSelectionUpdate === null || onSelectionUpdate === void 0 ? void 0 : onSelectionUpdate(updates.selectionUpdate);
        if (updates.agreementsChanged) onAgreementsChanged === null || onAgreementsChanged === void 0 ? void 0 : onAgreementsChanged();
    }
    // WHY: an undo comes back as chat-text, so the agreement lanes are refreshed off the undo turn itself
    const paneHandlers = _object_spread_props(_object_spread({}, handlers), {
        onUndo: async (messageId)=>{
            await conversation.undo(messageId);
            onAgreementsChanged === null || onAgreementsChanged === void 0 ? void 0 : onAgreementsChanged();
        }
    });
    return /*#__PURE__*/ _jsx(BulkPaneShell, {
        testId: BulkAmendmentAgentPaneTestIds.OpenPaneHeader(),
        isOpen: isOpen,
        conversation: conversation,
        handlers: paneHandlers,
        onSubmitPrompt: sendPrompt,
        clearChatBiName: "clear_bulk_amendment_agent_chat",
        toggleBiName: "toggle_bulk_amendment_agent_pane",
        onToggle: ()=>onOpenChange(!isOpen),
        onExpand: ()=>onOpenChange(true)
    });
};
